import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import {
  type DocumentParts,
  NOT_A_DOCUMENT_PART_SET_MESSAGE,
  deletedDocumentParts,
  isDocumentPartSet,
  mergeDocumentParts,
  packDocumentParts,
} from '@/helpers/docx/parts'
import { SweetspotError } from '@/helpers/error'
import { NOT_A_WORD_DOCUMENT_MESSAGE, isWordDocument } from '@/helpers/file'
import {
  type UploadUrlOut,
  computeSha256Hex,
  putToS3,
  retryWithBackoff,
} from '@/helpers/uploadUtils'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { type StoredDocx, fileQueryKeys } from '@/hooks/files/types'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'

import type { File as SweetFile } from '@/types/File'
import type { UUID } from '@/types/Id'

const requireAccessToken = (accessToken: string | undefined | null): string => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  return accessToken
}

/** Where a replacement is uploaded to: the file's own object, or a staged archive. */
type UploadUrlRoute = 'content/upload-url' | 'content/parts-upload-url'

const fetchUploadUrl = async (
  fileId: string,
  route: UploadUrlRoute,
  size: number,
  basedOn: string | null,
  contentSha256: string | null,
  accessToken: string,
): Promise<UploadUrlOut> => {
  // The size is signed into the URL that comes back, so a replacement the server
  // would refuse is refused now, while the file it would have landed on is still
  // there. The upload then has to be exactly this many bytes or S3 rejects it.
  const params = new URLSearchParams({ size: String(size) })
  // A whole document goes straight over the file's own object, so which version
  // it was built on is settled here — afterwards there is nothing left to ask.
  if (basedOn) {
    params.set('based_on', basedOn)
  }
  // Signed into the URL the same way, so S3 enforces it at PUT time and the
  // confirm step can trust the digest instead of re-reading the object for it.
  if (contentSha256) {
    params.set('content_sha256', contentSha256)
  }
  const res = await fetch(
    `${API_URL}/api/v1/files/${fileId}/${route}?${params.toString()}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return (await res.json()) as UploadUrlOut
}

const completeContentUpload = async (
  fileId: string,
  s3Key: string,
  basedOn: string | null,
  accessToken: string,
): Promise<SweetFile> => {
  const res = await fetch(`${API_URL}/api/v1/files/${fileId}/content`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ s3_key: s3Key, based_on: basedOn }),
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return convertDatesInObject(await res.json())
}

const completePartsUpload = async (
  fileId: string,
  s3Key: string,
  deletedPaths: string[],
  basedOn: string | null,
  accessToken: string,
): Promise<SweetFile> => {
  const res = await fetch(`${API_URL}/api/v1/files/${fileId}/content/parts`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      s3_key: s3Key,
      deleted_paths: deletedPaths,
      based_on: basedOn,
    }),
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
  return convertDatesInObject(await res.json())
}

/** Presign, PUT, confirm — the three steps a whole document takes to the file. */
const uploadWholeDocument = async (
  fileId: string,
  document: Blob,
  basedOn: string | null,
  accessToken: string,
): Promise<SweetFile> => {
  const contentSha256 = (await computeSha256Hex(document)) ?? null
  const upload = await retryWithBackoff(() =>
    fetchUploadUrl(
      fileId,
      'content/upload-url',
      document.size,
      basedOn,
      contentSha256,
      accessToken,
    ),
  )
  await retryWithBackoff(() => putToS3(upload, document))
  return await retryWithBackoff(() =>
    completeContentUpload(fileId, upload.s3_key, basedOn, accessToken),
  )
}

/** Whether a request came back with one particular status, rather than any. */
const failedWith = (error: unknown, status: number): boolean =>
  error instanceof SweetspotError &&
  typeof error.cause === 'object' &&
  error.cause !== null &&
  'status' in error.cause &&
  error.cause.status === status

/** Whether the server has no such route, rather than nothing to serve from it. */
const isMissingRoute = (error: unknown): boolean => failedWith(error, 404)

/**
 * Whether the file moved out from under this save.
 *
 * Both codes come to the same thing for whoever is waiting: what they are
 * sending is whole parts of a document, written out of bytes that are no longer
 * the file — 409 because somebody's save is landing this moment, 412 because
 * one already has. Sent anyway, they would take that person's work back out.
 */
const isStaleBasis = (error: unknown): boolean =>
  failedWith(error, 409) || failedWith(error, 412)

export const FILE_CHANGED_MESSAGE =
  "Someone else saved changes to this file, so yours weren't saved. Reload the page and make your changes again."

export interface UseReplaceFileContentArgs {
  fileId: string
  /** Refreshes the pursuit's file list, where the file's size and date show. */
  pursuitId?: UUID | ''
}

/**
 * What a save wrote: the whole document, or only the parts of it that changed.
 *
 * Both end up as the same stored file. Sending parts spares a reviewer who
 * added one comment from uploading the images they never touched, which on a
 * large proposal is the difference between a moment and a minute.
 */
export type FileContentReplacement = Blob | DocumentParts

export interface ReplacedFileContent {
  file: SweetFile
  /**
   * The document as it now stands, or null when this browser no longer holds a
   * copy that is one. Null is not nothing to do: a copy left over from before
   * the save is a document without the comment that was just made, and handing
   * it to whoever opens the file next would lose that comment on their save.
   */
  bytes: Blob | null
}

/**
 * Writes a document over a stored file, leaving everything about it in place.
 *
 * Same three steps as an upload — presign, PUT to S3, confirm — but against the
 * file's own object rather than a new one, so it stays the same file: same id,
 * same permissions, same place in the folder it was put in. The server derives
 * nothing from what it receives, which is what makes this cheap enough to run
 * every time a reviewer leaves a comment.
 *
 * What arrives is checked to be a document before any of that happens. There is
 * no undo on the other side of this call, and an exporter that fails quietly
 * hands back a few bytes of empty zip — which would otherwise land on top of a
 * proposal somebody spent a week on.
 */
export const useReplaceFileContent = ({
  fileId,
  pursuitId = '',
}: UseReplaceFileContentArgs) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<ReplacedFileContent, Error, FileContentReplacement>({
    mutationFn: async (replacement) => {
      const accessToken = requireAccessToken(user?.accessToken)
      // The copy this browser has open, and the version of the stored file it
      // was downloaded from. What is being sent was written out of those bytes,
      // and saying so is what stops it landing on somebody else's work.
      const opened = queryClient.getQueryData<StoredDocx>(
        fileQueryKeys.docx({ fileId }),
      )
      const basedOn = opened?.version ?? null

      if (replacement instanceof Blob) {
        if (!(await isWordDocument(replacement))) {
          throw new Error(NOT_A_WORD_DOCUMENT_MESSAGE)
        }
        const file = await uploadWholeDocument(
          fileId,
          replacement,
          basedOn,
          accessToken,
        )
        return { file, bytes: replacement }
      }

      if (!isDocumentPartSet(replacement)) {
        throw new Error(NOT_A_DOCUMENT_PART_SET_MESSAGE)
      }

      // Merging locally is for this browser's copy alone — the stored file is
      // the server's to merge — so it runs alongside the upload rather than
      // after it, and the save is over when the upload is.
      //
      // A merge that fails costs this browser its copy, and nothing more: the
      // save itself is already done by the time it is waited on.
      const merging = opened
        ? mergeDocumentParts(opened.blob, replacement).catch(() => null)
        : Promise.resolve(null)

      const archive = await packDocumentParts(replacement)
      let upload: UploadUrlOut
      try {
        upload = await retryWithBackoff(() =>
          fetchUploadUrl(
            fileId,
            'content/parts-upload-url',
            archive.size,
            // Nothing is written over until the confirmation below, which is
            // where the version is checked, under the hold that makes it mean
            // something.
            null,
            // The confirm side for this route hashes the spliced result in
            // memory, never rereads S3 for it — nothing here to shortcut.
            null,
            accessToken,
          ),
        )
      } catch (error) {
        // A server from before this route knows nothing about parts, which a
        // browser left open across a deploy will meet — and a reader is not
        // going to lose the comment they just wrote over that. The merged
        // document is the same document, so it goes up whole instead.
        const whole = isMissingRoute(error) ? await merging : null
        if (!whole) {
          throw error
        }
        const file = await uploadWholeDocument(
          fileId,
          whole,
          basedOn,
          accessToken,
        )
        return { file, bytes: whole }
      }
      await retryWithBackoff(() => putToS3(upload, archive))
      const file = await retryWithBackoff(() =>
        completePartsUpload(
          fileId,
          upload.s3_key,
          deletedDocumentParts(replacement),
          basedOn,
          accessToken,
        ),
      )
      return { file, bytes: await merging }
    },
    onSuccess: ({ file, bytes }) => {
      // The document just written is the file now, so whoever opens it next
      // reads it from here instead of downloading what they already have.
      if (bytes) {
        queryClient.setQueryData(fileQueryKeys.docx({ fileId }), {
          blob: bytes,
          version: file.content_version ?? null,
          versionId: file.current_version_id ?? null,
        } satisfies StoredDocx)
      } else {
        queryClient.removeQueries({ queryKey: fileQueryKeys.docx({ fileId }) })
      }
      queryClient.setQueryData(fileQueryKeys.file({ fileId }), file)
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: fileQueryKeys.versions({ fileId }),
        }),
        ...(pursuitId
          ? [
              queryClient.invalidateQueries({
                queryKey: pursuitFilesQueryKeys.pursuit({ pursuitId }),
              }),
            ]
          : []),
      ])
    },
    onError: (error) => {
      if (isStaleBasis(error)) {
        // The copy in hand stays exactly as it is, stale version and all. It is
        // what the open editor is still showing, and dropping it would have the
        // next save quote a version downloaded after this one was refused —
        // which the server would accept, over the write it was refused for.
        handleError(error, false, FILE_CHANGED_MESSAGE)
        return
      }
      handleError(error)
    },
  })
}
